import React, { useEffect, useRef, useState } from "react";
import "./header.scss";
import { NavLink, Link } from "react-router-dom";
import header_logo from "../../assets/images/header_logo.png";
import crossLogo from "../../assets/icons/cross_png.png";
import apiFetch from "../../utils/apiFetch";
import menuIcon from "../../assets/icons/menu.png";
import closeIcon from "../../assets/icons/cross_png.png";

const DEBOUNCE_MS = 300;

const Header = () => {
  const [searchValue, setSearchValue] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  const [menuOpen, setMenuOpen] = useState(false);

  const searchRef = useRef(null);          // общий контейнер поиска (input + list)
  const abortRef = useRef(null);           // текущий AbortController
  const debounceRef = useRef(null);        // таймер debounce
  const inputRef = useRef(null);

  const firstVoltageKv = (raw) => {
    if (raw === null || raw === undefined) return null;
    const firstPart = String(raw).trim().split("/")[0]?.trim() ?? "";
    const m = firstPart.match(/\d+/);
    if (!m) return null;
    const kv = Number(m[0]);
    return Number.isFinite(kv) ? kv : null;
  };

  const formatSearchLabel = (result) => {
    if (result.type === "city") return result.data?.name ?? "—";

    if (result.type === "pole") {
      const d = result.data;
      const netName = d.network?.name ?? "";
      const kv = firstVoltageKv(d.network?.voltage);
      const cityName = d.city?.name ?? "";
      return `Таянч №${d.name}${netName ? ` — ${netName}` : ""}${kv ? ` ${kv}кВ` : ""}${cityName ? ` ${cityName}` : ""}`;
    }

    const name = result.data?.name ?? "—";
    const kv = firstVoltageKv(result.data?.voltage);
    const cityName = result.data?.city?.name;

    return `${name}${kv ? ` ${kv}кВ` : ""}${cityName ? ` ${cityName}` : ""}`;
  };

  // ====== debounce + abort ======
  useEffect(() => {
    const q = searchValue.trim();

    // очистка, если пусто или < 2 символов
    if (!q || q.length < 2) {
      // отменяем активный запрос
      if (abortRef.current) abortRef.current.abort();
      abortRef.current = null;

      setIsSearching(false);
      setSearchResults([]);
      return;
    }

    // debounce
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      // отменяем предыдущий запрос перед новым
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setIsSearching(true);

      try {
        // Parse pole search: "116 л-504" or "116 " (digits + space)
        const raw = searchValue;
        const poleMatch = raw.match(/^(\d+)\s(.*)$/);

        let url = `/api/search?query=${encodeURIComponent(q)}&limit=15`;
        if (poleMatch) {
          const poleName = poleMatch[1];
          const lineQ = poleMatch[2].trim();
          url += `&pole=${encodeURIComponent(poleName)}`;
          if (lineQ) url += `&line=${encodeURIComponent(lineQ)}`;
        }

        const response = await apiFetch(url, { signal: controller.signal });

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const data = await response.json();
        setSearchResults(Array.isArray(data) ? data : []);
      } catch (error) {
        // AbortError — это нормально, просто игнорим
        if (error?.name !== "AbortError") {
          console.error("Error during search:", error);
        }
      } finally {
        // важно: isSearching выключаем только если это не отменённый запрос
        if (!controller.signal.aborted) setIsSearching(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchValue]);

  // ====== click outside ======
  useEffect(() => {
    const onDown = (e) => {
      const root = searchRef.current;
      if (!root) return;

      if (!root.contains(e.target)) {
        setSearchResults([]);
        setIsSearching(false);
      }
    };

    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);

    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [menuOpen]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handleSubmit = (e) => e.preventDefault();

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
  };

  const handleResultClick = (result) => {
    setSearchValue(formatSearchLabel(result));
    setSearchResults([]);
  };

  const clearAll = () => {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = null;

    setSearchResults([]);
    setSearchValue("");
    setIsSearching(false);

    // вернуть фокус в инпут
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const buildLink = (result) => {
    if (result.type === "city") return `/indicators/${result.data.slug}`;
    if (result.type === "substation") return `/indicators/substations/${result.data.slug}`;
    if (result.type === "pole") {
      const networkSlug = result.data.network?.slug ?? "";
      const poleId = result.data.id;
      return `/indicators/networks/${networkSlug}/poles/${poleId}`;
    }
    return `/indicators/networks/${result.data.slug}`;
  };

  const showDropdown = searchValue.trim().length > 0 && (isSearching || searchResults.length > 0);

  const tickerText = "⚠ Тестовый режим \u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0";

  return (
    <>
      <header className="header">
        <div className="test-banner" aria-hidden="true">
          <span className="test-banner__track">{tickerText.repeat(30)}</span>
        </div>
        <div className="container">
          <Link to="/" className="header-logo">
            <img className="header-logo__img" src={header_logo} alt="logo" />
          </Link>

          <div className="header-wrapper d-flex align-items-center">
            <ul className="header-list header-list--mobile align-items-center">
              <li className="header-list__item">
                <NavLink
                  className={({ isActive }) =>
                    "header-list__link" + (isActive ? " is-active" : "")
                  }
                  to="/accidents"
                  end
                >
                  Ўчишлар
                </NavLink>
              </li>
              <li className="header-list__item">
                <NavLink
                  className={({ isActive }) =>
                    "header-list__link" + (isActive ? " is-active" : "")
                  }
                  to="/substations"
                >
                  Маълумот қўшиш
                </NavLink>
              </li>
              <li className="header-list__item">
                <NavLink
                  className={({ isActive }) =>
                    "header-list__link" + (isActive ? " is-active" : "")
                  }
                  to="/map"
                >
                  Харита
                </NavLink>
              </li>
              <li className="header-list__item">
                <NavLink
                  className={({ isActive }) =>
                    "header-list__link" + (isActive ? " is-active" : "")
                  }
                  to="/checkups/add"
                >
                  Кўрик қўшиш
                </NavLink>
              </li>
              <li className="header-list__item">
                <NavLink
                  className={({ isActive }) =>
                    "header-list__link" + (isActive ? " is-active" : "")
                  }
                  to="/checkups"
                  end
                >
                  Кўрик
                </NavLink>
              </li>
              <li className="header-list__item">
                <NavLink
                  className={({ isActive }) =>
                    "header-list__link" + (isActive ? " is-active" : "")
                  }
                  to="/monitoring"
                  end
                >
                  Мониторинг
                </NavLink>
              </li>
              <li className="header-list__item">
                <NavLink
                  to="/statistics"
                  className={({ isActive }) => "header-list__link" + (isActive ? " is-active" : "")}
                >
                  Статистика
                </NavLink>
              </li>
            </ul>

            <form className="header-search" onSubmit={handleSubmit} ref={searchRef}>
              <input
                ref={inputRef}
                className="header-search__input"
                type="text"
                placeholder="Излаш..."
                value={searchValue}
                onChange={handleSearchChange}
                autoComplete="off"
              />

              {searchValue.length > 0 && (
                <button
                  type="button"
                  onClick={clearAll}
                  className="header-search__button"
                  aria-label="Тозалаш"
                >
                  <img src={crossLogo} alt="" />
                </button>
              )}

              {showDropdown && (
                <ul className="header-search__list">
                  {isSearching && (
                    <li className="header-search__item header-search__item--muted">
                      Қидирилмоқда…
                    </li>
                  )}

                  {!isSearching && searchResults.map((result) => (
                    <li
                      key={`${result.type}-${result.data.id}`}
                      className="header-search__item"
                      onClick={() => handleResultClick(result)}
                    >
                      <Link className="header-search__link" to={buildLink(result)}>
                        {formatSearchLabel(result)}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </form>
            <button
              type="button"
              className="header-menu-btn"
              onClick={() => setMenuOpen(true)}
              aria-label="Меню"
            >
              <img src={menuIcon} alt="" />
            </button>
          </div>
        </div>
      </header>
      {menuOpen && (
        <div className={"menu-drawer" + (menuOpen ? " is-open" : "")} aria-hidden={!menuOpen}>
          <div className="menu-drawer__overlay" onClick={() => setMenuOpen(false)} />

          <aside className="menu-drawer__panel" role="dialog" aria-modal="true">
            <div className="menu-drawer__top">
              <div className="menu-drawer__title">Меню</div>

              <button
                type="button"
                className="menu-drawer__close"
                onClick={() => setMenuOpen(false)}
                aria-label="Ёпиш"
              >
                <img src={closeIcon} alt="" />
              </button>
            </div>

            <nav className="menu-drawer__nav">
              <NavLink
                to="/accidents"
                end
                className={({ isActive }) => "menu-drawer__link" + (isActive ? " is-active" : "")}
                onClick={() => setMenuOpen(false)}
              >
                Ўчишлар
              </NavLink>

              <NavLink
                to="/substations"
                className={({ isActive }) => "menu-drawer__link" + (isActive ? " is-active" : "")}
                onClick={() => setMenuOpen(false)}
              >
                Маълумот қўшиш
              </NavLink>

              <NavLink
                to="/map"
                className={({ isActive }) => "menu-drawer__link" + (isActive ? " is-active" : "")}
                onClick={() => setMenuOpen(false)}
              >
                Харита
              </NavLink>

              <NavLink
                to="/checkups/add"
                className={({ isActive }) => "menu-drawer__link" + (isActive ? " is-active" : "")}
                onClick={() => setMenuOpen(false)}
              >
                Кўрик қўшиш
              </NavLink>

              <NavLink
                to="/checkups"
                end
                className={({ isActive }) => "menu-drawer__link" + (isActive ? " is-active" : "")}
                onClick={() => setMenuOpen(false)}
              >
                Кўрик
              </NavLink>
              <NavLink
                to="/monitoring"
                end
                className={({ isActive }) => "menu-drawer__link" + (isActive ? " is-active" : "")}
                onClick={() => setMenuOpen(false)}
              >
                Мониторинг
              </NavLink>
              <NavLink
                to="/statistics"
                className={({ isActive }) => "menu-drawer__link" + (isActive ? " is-active" : "")}
                onClick={() => setMenuOpen(false)}
              >
                Статистика
              </NavLink>
            </nav>
          </aside>
        </div>
      )}
    </>
  );
};

export default Header;
